import { useEffect, useMemo, useState } from "react";
import { KitList, Sentence } from "./kitList";
import { MODEL_USER } from "./consts";
import { getReadAudioUrl } from "./storage";

export type PlayWord = {
  word: string;
  isPlaying: boolean;
};

export const usePlay = (audio: HTMLAudioElement, kitList: KitList) => {
  const [currentWord, setCurrentWord] = useState<PlayWord | null>(null);

  const allWords = useMemo(
    () =>
      kitList.sections
        .flatMap(({ sentences }) => sentences as Sentence[])
        .map(({ word }) => word),
    [kitList]
  );

  const toggle = () => {
    if (audio.paused) {
      audio.play();
    } else {
      audio.pause();
    }
    setCurrentWord((prev) => ({ ...prev!, isPlaying: !audio.paused }));
  };

  const playWord = (word: string) => {
    if (word === currentWord?.word) {
      toggle();
      return;
    }

    setCurrentWord({ word, isPlaying: true });
    audio.src = getReadAudioUrl(MODEL_USER, word);
    audio.play();
  };

  const stop = () => {
    audio.pause();
    setCurrentWord(null);
  };

  useEffect(() => {
    const controller = new AbortController();
    const playPrevNext = (offset: -1 | 1) => {
      const currentIndex = allWords.findIndex((w) => w === currentWord?.word);
      const newIndex = currentIndex + offset;
      const newWord = allWords[newIndex];
      if (newWord) {
        playWord(newWord);
      }
    };

    const hotkeys = {
      " ": () => {
        if (!currentWord) {
          playWord(allWords[0]);
          return;
        }

        toggle();
      },
      Enter: () => {
        audio.currentTime = 0;
      },
      n: () => playPrevNext(1),
      p: () => playPrevNext(-1),
    };

    window.addEventListener(
      "keydown",
      (event) => {
        const action = hotkeys[event.key as keyof typeof hotkeys];
        if (action) {
          event.preventDefault();
          action();
        }
      },
      { signal: controller.signal }
    );

    return () => controller.abort();
  }, [currentWord, playWord]);

  useEffect(() => {
    if (!currentWord || !currentWord.isPlaying) {
      return;
    }

    const controller = new AbortController();
    audio.addEventListener(
      "ended",
      () => setCurrentWord((prev) => ({ ...prev!, isPlaying: false })),
      { signal: controller.signal }
    );
    return () => controller.abort();
  }, [currentWord, audio]);

  return [currentWord, playWord, stop, toggle] as const;
};
